import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import Image from "next/image";
import type WaveSurfer from "wavesurfer.js";
import {
  PlayerContainer,
  PlayButton,
  WaveformContainer
} from "./audio-player.styles";
import { StreamingEqualizer } from "./streaming-equalizer";
import {
  AudioStreamingStore,
  AudioStreamingStopReason,
  AudioStreamingTrackingData
} from "./audio-streaming-store";
import AudioPlayIcon from "public/assets-v2/graphicons/general/audio_play.svg";
import AudioPauseIcon from "public/assets-v2/graphicons/general/audio_pause.svg";

type AudioPlayerProps = {
  voiceUrl?: string;
  websocketUrl?: string;
  messagePayload?: object;
  accessToken?: string;
  blockPlaying?: boolean;
  onAudioPlayed?: () => void;
  onStreamingStarted?: () => void;
  onStreamingStopped?: (
    reason: AudioStreamingStopReason,
    data: AudioStreamingTrackingData
  ) => void;
  onStreamingVoiceUrlReady?: (voiceUrl: string) => void;
};

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  voiceUrl,
  websocketUrl,
  messagePayload,
  accessToken,
  blockPlaying = false,
  onAudioPlayed,
  onStreamingStarted,
  onStreamingStopped,
  onStreamingVoiceUrlReady
}) => {
  if (voiceUrl) {
    return (
      <WaveSurferPlayer
        url={voiceUrl}
        blockPlaying={blockPlaying}
        onAudioPlayed={onAudioPlayed}
      />
    );
  }

  if (websocketUrl && messagePayload && accessToken) {
    return (
      <StreamingPlayer
        websocketUrl={websocketUrl}
        messagePayload={messagePayload}
        accessToken={accessToken}
        blockPlaying={blockPlaying}
        onAudioPlayed={onAudioPlayed}
        onStreamingStarted={onStreamingStarted}
        onStreamingStopped={onStreamingStopped}
        onStreamingVoiceUrlReady={onStreamingVoiceUrlReady}
      />
    );
  }

  return null;
};

// --- WaveSurfer Mode ---

const WaveSurferPlayer: React.FC<{
  url: string;
  blockPlaying: boolean;
  onAudioPlayed?: () => void;
}> = ({ url, blockPlaying, onAudioPlayed }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const hasTrackedPlay = useRef(false);

  useEffect(() => {
    if (!waveformRef.current) return;

    let cancelled = false;
    let ws: WaveSurfer | null = null;

    const initWaveSurfer = async () => {
      const WaveSurfer = (await import("wavesurfer.js")).default;
      if (cancelled) return;

      ws = WaveSurfer.create({
        container: waveformRef.current!,
        barWidth: 2,
        barGap: 2,
        barRadius: 2,
        height: 40,
        cursorWidth: 0,
        waveColor: "#B8C7E0",
        progressColor: "#1849D6"
      });

      ws.on("finish", () => setIsPlaying(false));
      waveSurferRef.current = ws;

      ws.load(url)
        .then(() => setIsWaveformReady(true))
        .catch((e: unknown) => {
          Sentry.captureException(e, {
            tags: { component: "AudioPlayer", detail: "load error" }
          });
          setIsWaveformReady(true);
        });
    };

    initWaveSurfer();

    return () => {
      cancelled = true;
      ws?.destroy();
    };
  }, [url]);

  useEffect(() => {
    if (blockPlaying && waveSurferRef.current) {
      waveSurferRef.current.pause();
      setIsPlaying(false);
    }
  }, [blockPlaying]);

  const handlePlay = async () => {
    const ws = waveSurferRef.current;
    if (!ws) return;

    if (isPlaying) {
      ws.pause();
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    if (!hasTrackedPlay.current) {
      hasTrackedPlay.current = true;
      onAudioPlayed?.();
    }
    try {
      await ws.play();
    } catch (e) {
      setIsPlaying(false);
      Sentry.captureException(e, {
        tags: { component: "AudioPlayer", detail: "play error" }
      });
    }
  };

  return (
    <PlayerContainer>
      <PlayButton
        type="button"
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
        onClick={handlePlay}
      >
        <Image
          src={isPlaying ? AudioPauseIcon : AudioPlayIcon}
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
      </PlayButton>
      <WaveformContainer ref={waveformRef} $visible={isWaveformReady} />
    </PlayerContainer>
  );
};

// --- Streaming Mode ---

const StreamingPlayer: React.FC<{
  websocketUrl: string;
  messagePayload: object;
  accessToken: string;
  blockPlaying: boolean;
  onAudioPlayed?: () => void;
  onStreamingStarted?: () => void;
  onStreamingStopped?: (
    reason: AudioStreamingStopReason,
    data: AudioStreamingTrackingData
  ) => void;
  onStreamingVoiceUrlReady?: (voiceUrl: string) => void;
}> = ({
  websocketUrl,
  messagePayload,
  accessToken,
  blockPlaying,
  onAudioPlayed,
  onStreamingStarted,
  onStreamingStopped,
  onStreamingVoiceUrlReady
}) => {
  const storeRef = useRef<AudioStreamingStore | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [chunksReceived, setChunksReceived] = useState(0);
  const [streamingComplete, setStreamingComplete] = useState(false);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const hasTrackedPlay = useRef(false);

  useEffect(() => {
    return () => {
      if (storeRef.current) {
        storeRef.current.dispose();
        storeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (blockPlaying) {
      storeRef.current?.pause();
      setIsPlaying(false);
    }
  }, [blockPlaying]);

  if (streamingComplete && voiceUrl) {
    return (
      <WaveSurferPlayer
        url={voiceUrl}
        blockPlaying={blockPlaying}
        onAudioPlayed={() => {
          if (!hasTrackedPlay.current) {
            hasTrackedPlay.current = true;
            onAudioPlayed?.();
          }
        }}
      />
    );
  }

  const handlePlay = async () => {
    if (blockPlaying || isInitializing) return;

    let store = storeRef.current;

    if (!store) {
      store = new AudioStreamingStore({
        websocketUrl,
        messagePayload,
        accessToken,
        onChunkReceived: (chunkNumber) => setChunksReceived(chunkNumber),
        onPlaybackComplete: (audioUrl) => {
          setIsPlaying(false);
          setStreamingComplete(true);
          setVoiceUrl(audioUrl);
          if (audioUrl) onStreamingVoiceUrlReady?.(audioUrl);
        },
        onPlaybackStarted: () => {
          if (!hasTrackedPlay.current) {
            hasTrackedPlay.current = true;
            onAudioPlayed?.();
          }
          setIsPlaying(true);
        },
        onPlaybackPaused: () => setIsPlaying(false),
        onStreamingStarted,
        onStreamingStopped
      });
      storeRef.current = store;
      setIsInitializing(true);

      try {
        await store.initialize();
      } catch (error) {
        store.dispose();
        storeRef.current = null;
        Sentry.captureException(error, {
          tags: {
            component: "AudioPlayer",
            detail: "streaming initialization error"
          }
        });
        return;
      } finally {
        setIsInitializing(false);
      }
    }

    if (isPlaying) {
      await store.pause();
      setIsPlaying(false);
    } else {
      await store.play();
      setIsPlaying(true);
    }
  };

  return (
    <PlayerContainer>
      <PlayButton
        type="button"
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
        onClick={handlePlay}
      >
        <Image
          src={isPlaying ? AudioPauseIcon : AudioPlayIcon}
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
      </PlayButton>
      {!streamingComplete && (
        <StreamingEqualizer
          isPlaying={isPlaying}
          chunksReceived={chunksReceived}
        />
      )}
    </PlayerContainer>
  );
};
